import { Component, Input, OnInit, inject, signal } from '@angular/core';
import {
  Observable,
  Subject,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  tap
} from 'rxjs';

import { User } from '@core/models';
import { Company } from '@core/models/company/company.model';

import { AuthService } from '@core/services/auth/auth.service';
import { COMPANY_SEARCH } from '@core/services/company-search';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

const SEARCH_DEBOUNCE_MS = 300;

/*
 * Allows super admins and multi company users to switch their instance.
 */
@Component({
  imports: [SelectModule, CommonModule, FormsModule],
  standalone: true,
  selector: 'app-main-company-selector',
  templateUrl: 'main-company-selector.component.html'
})
export class MainCompanySelectorComponent implements OnInit {
  private companySearch = inject(COMPANY_SEARCH);
  private authService = inject(AuthService);
  private searchTerms = new Subject<string>();

  @Input() user: User;
  @Input() selectedCompany?: Company;

  companies$: Observable<Company[]>;

  // The select's own filter must not keep an id match the search itself would drop.
  protected filterBy = this.companySearch.matchesId ? 'name,id' : 'name';

  protected readonly maxResults = this.companySearch.maxResults;

  /** Whether the last search hit its cap, so the companies past it are reached only by typing. */
  readonly capped = signal(false);

  ngOnInit() {
    this.companies$ = this.searchTerms.pipe(
      debounceTime(SEARCH_DEBOUNCE_MS),
      startWith(''),
      distinctUntilChanged(),
      switchMap((q) =>
        this.companySearch
          .search(this.user, q)
          .pipe(tap((companies) => this.capped.set(this.reachesCap(companies))))
      ),
      map((companies) => this.keepSelected(companies))
    );
  }

  search(q: string) {
    this.searchTerms.next(q.trim());
  }

  setActiveCompany(event: SelectChangeEvent) {
    if (event.value.active)
      this.authService.changeCompany(event.value).subscribe();
  }

  private reachesCap(companies: Company[]): boolean {
    return this.maxResults !== undefined && companies.length >= this.maxResults;
  }

  // The select shows the current company's name only while it is among the options, and a search
  // need not return it. An inactive company is never offered, so it is not added either.
  private keepSelected(companies: Company[]): Company[] {
    const selected = this.selectedCompany;
    if (!selected?.active || companies.some((c) => c.id === selected.id)) {
      return companies;
    }
    return [selected, ...companies];
  }
}
